import { Check } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';
import { problemCode, problemMessage } from '../api/problem';
import { RoleChecklist } from '../roles/RoleChecklist';
import { useAssignableRoles } from '../roles/use-assignable-roles';
import { SidePanel } from '../ui/SidePanel';
import type { Invitation } from './use-invitations';

function useEditRolesForm({
  invitation,
  onOpenChange,
  placeKind,
  placeId,
  onSaved,
}: {
  readonly invitation: Invitation | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation('administration');
  const { pending, run } = usePendingWrite();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Each opening starts from the invitation's own roles.
  const invitationId = invitation?.id;
  useEffect(() => {
    setSelected(new Set(invitation?.roles.map((role) => role.id) ?? []));
    setError(null);
  }, [invitationId]);

  const initial = new Set(invitation?.roles.map((role) => role.id) ?? []);
  const unchanged =
    selected.size === initial.size && [...selected].every((roleId) => initial.has(roleId));

  const toggle = (roleId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (invitation === null) {
      return;
    }
    if (selected.size === 0) {
      setError(t('editInvitationRoles.rolesRequired'));
      return;
    }
    void run(async () => {
      const { error: problem, response } = await apiClient.PUT(
        '/v1/administration/{placeKind}/{placeId}/invitations/{invitationId}/roles',
        {
          params: { path: { placeKind, placeId, invitationId: invitation.id } },
          body: { roleIds: [...selected] },
        },
      );
      if (!response.ok) {
        // No longer pending or no longer there: nothing to edit; say so and read the list again.
        const code = problemCode(problem);
        if (code === 'INVITATION_NOT_PENDING' || code === 'INVITATION_NOT_FOUND') {
          toast.error(problemMessage(t, problem, response.status));
          onOpenChange(false);
          onSaved();
          return;
        }
        setError(problemMessage(t, problem, response.status));
        return;
      }
      toast.success(t('editInvitationRoles.done', { email: invitation.email }));
      onOpenChange(false);
      onSaved();
    });
  };

  return { pending, selected, error, unchanged, toggle, submit };
}

function EditRolesField({
  roles,
  retry,
  selected,
  onToggle,
  placeName,
}: {
  readonly roles: ReturnType<typeof useAssignableRoles>['state'];
  readonly retry: () => void;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (roleId: string) => void;
  readonly placeName: string;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="field-label">{t('invite.roles', { place: placeName })}</span>
        <span className="text-muted-foreground text-[12.5px]">
          {t('invite.chosen', { count: selected.size })}
        </span>
      </div>
      {roles.status === 'loading' ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t('common.loading')}
        </p>
      ) : null}
      {roles.status === 'error' ? (
        <p role="alert" className="flex items-center gap-3 text-sm">
          {t('invite.rolesError')}
          <Button variant="outline" size="sm" onClick={retry}>
            {t('common.retry')}
          </Button>
        </p>
      ) : null}
      {roles.status === 'ready' ? (
        <RoleChecklist
          roles={roles.roles}
          selected={selected}
          onToggle={onToggle}
          legend={t('invite.roles', { place: placeName })}
        />
      ) : null}
    </div>
  );
}

/**
 * «Modifica ruoli» on a pending invitation (decision 151, #550): the same role picker as Invita,
 * with the invitation's roles already ticked. Saving sends the whole new set
 * (`PUT …/invitations/{id}/roles`); the address, the inviter and the expiry stay as they are.
 *
 * Before, the only way was to withdraw the invitation and invite again. An invitation that stopped
 * being pending while the panel was open (accepted, withdrawn, expired) closes the panel and reads
 * the list again, with a toast that says why.
 */
export function EditInvitationRolesPanel({
  invitation,
  onOpenChange,
  placeKind,
  placeId,
  placeName,
  onSaved,
}: {
  readonly invitation: Invitation | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation('administration');
  const { state: roles, retry } = useAssignableRoles(placeKind, placeId);
  const formId = useId();
  const open = invitation !== null;
  const form = useEditRolesForm({ invitation, onOpenChange, placeKind, placeId, onSaved });
  const { pending, selected, error, unchanged, toggle } = form;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('editInvitationRoles.title')}
      description={
        invitation !== null
          ? t('editInvitationRoles.summary', { email: invitation.email, place: placeName })
          : ''
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('invite.cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={pending || unchanged}>
            <Check />
            {t('editInvitationRoles.submit')}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={form.submit} className="flex flex-col gap-5">
        <EditRolesField
          roles={roles}
          retry={retry}
          selected={selected}
          onToggle={toggle}
          placeName={placeName}
        />
        <p className="invite-note">{t('editInvitationRoles.notice')}</p>
        {error !== null ? (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
